/**
 * Arms that hold the rifle wherever a pose puts it (ROUND7 §3). The upright poses write the arm angles by hand
 * for a chest that is nearly vertical; a man bent double over his knees would hold his rifle into the ground
 * with those. So here the rifle is placed in the BODY's frame - where it belongs whatever the chest is doing -
 * and each arm is solved to reach it: the right hand to the grip, the left to the handguard.
 *
 * The chest (the spine bone) must be leaned by `lean` about x and not yet twisted when these run: the twist
 * toward the aim that `applyPose` adds afterwards turns the chest, the arms and the rifle together.
 */

import { Vector3 } from 'three';
import type { BoneOf } from './poseBase';
import { B } from './rig';

/** Shoulder to elbow (rig.ts REST), and elbow to the middle of the hand (model.ts: the hand box). */
const UPPER = 0.3;
const FORE = 0.33;
/** Where the hands hold the rifle, in the weapon's frame (model.ts: grip under the action, handguard ahead). */
const GRIP = new Vector3(0, -0.1, 0.1);
const HANDGUARD = new Vector3(0, -0.03, 0.3);
/** The shoulders, in the chest's frame (rig.ts REST). */
const SHOULDER_L = new Vector3(0.3, 0.42, 0);
const SHOULDER_R = new Vector3(-0.3, 0.42, 0);

const X_AXIS = new Vector3(1, 0, 0);
const Z_AXIS = new Vector3(0, 0, 1);
/* Scratch: nothing is allocated per frame. */
const _to = new Vector3();
const _pole = new Vector3();
const _elbow = new Vector3();
const _dir = new Vector3();
const _grip = new Vector3();
const _guard = new Vector3();

const clamp1 = (v: number): number => Math.max(-1, Math.min(1, v));

/** Turn a limb whose rest direction is -y to point along unit `d` (its parent's frame): rotation about x, then z. */
function pointLimb(bone: BoneOf, index: number, d: Vector3): { x: number; z: number } {
  const z = Math.asin(clamp1(d.x));
  const x = Math.atan2(-d.z, -d.y);
  bone(index).rotation.set(x, 0, z);
  return { x, z };
}

/**
 * Solve one arm so the hand reaches `target` (chest frame). The elbow bends toward the ground and out to his
 * side (`side` +1 left, -1 right) - toward the WORLD's ground, which for a chest leaned far forward is its own
 * front. Out of reach, the arm points straight at the target.
 */
function reach(
  bone: BoneOf, upper: number, fore: number, shoulder: Vector3, target: Vector3, side: number, lean: number,
): void {
  const to = _to.subVectors(target, shoulder);
  const dist = Math.min(Math.max(to.length(), 1e-4), UPPER + FORE - 1e-4);
  to.normalize();
  const pole = _pole.set(side * 0.7, -Math.cos(lean), Math.sin(lean) - 0.3);
  pole.addScaledVector(to, -pole.dot(to)).normalize();
  const cos = clamp1((UPPER * UPPER + dist * dist - FORE * FORE) / (2 * UPPER * dist));
  const elbow = _elbow.copy(shoulder).addScaledVector(to, UPPER * cos);
  elbow.addScaledVector(pole, UPPER * Math.sqrt(1 - cos * cos));
  const r = pointLimb(bone, upper, _dir.subVectors(elbow, shoulder).normalize());
  // The forearm's direction in the upper arm's own frame: undo its rotation (x, then z) in reverse.
  const d = _dir.subVectors(target, elbow).normalize().applyAxisAngle(X_AXIS, -r.x).applyAxisAngle(Z_AXIS, -r.z);
  pointLimb(bone, fore, d);
}

/**
 * Put the rifle where the body wants it and both hands on it: the weapon's origin at `at` (body axes, metres
 * from the chest's pivot), its muzzle `pitch` below level and `yaw` across toward his left, whatever the
 * chest's `lean`.
 */
export function holdRifle(
  bone: BoneOf, lean: number, at: readonly [number, number, number], pitch: number, yaw: number,
): void {
  const c = Math.cos(lean);
  const s = Math.sin(lean);
  const [x, y, z] = at;
  const w = bone(B.weapon);
  w.position.set(x, y * c + z * s, -y * s + z * c);
  w.rotation.set(pitch - lean, yaw, 0);
  const grip = _grip.copy(GRIP).applyEuler(w.rotation).add(w.position);
  const guard = _guard.copy(HANDGUARD).applyEuler(w.rotation).add(w.position);
  reach(bone, B.rUpperArm, B.rForeArm, SHOULDER_R, grip, -1, lean);
  reach(bone, B.lUpperArm, B.lForeArm, SHOULDER_L, guard, 1, lean);
}

/** Rifle in the shoulder and level, stock at the right shoulder under his cheek, however far the chest leans. */
export function shoulderRifle(bone: BoneOf, lean: number): void {
  // The upright `shouldered` pose has the weapon 0.16 in, 0.06 down and 0.26 ahead of the right shoulder.
  holdRifle(bone, lean, [-0.14, SHOULDER_R.y * Math.cos(lean) - 0.06, SHOULDER_R.y * Math.sin(lean) + 0.26], 0, 0);
}
